import React, { useState, useEffect } from 'react';
import { Layout, Typography, Card, Row, Col, Select, Button, Timeline, Divider, Statistic, Tag, Space, Alert } from 'antd';
import { FileTextOutlined, CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons';
import file1 from '../assets/data/Quelle 1_Original_Mobbing als immaterieller Schaden_BAG 2016.txt';
import file2 from '../assets/data/Quelle 2_Original_Mobbing als immaterieller Schaden_BAG 2008.txt';

const { Content } = Layout;
const { Title, Paragraph, Text } = Typography;
const { Option } = Select;

// A simple function to read a text file
const readTextFile = async (file: string) => {
  const response = await fetch(file);
  const text = await response.text();
  return text;
};

// Mock keyword data for each file (in a real app, this would come from an API)
const getKeywordsData = (filePath: string | null) => {
  if (filePath === file1) {
    return [
      'Mobbing', 'immaterieller Schaden', 'Arbeitsrecht', 'Schadensersatz',
      'Betriebsfrieden', 'Abmahnung', 'Kündigung', 'Arbeitsgericht',
      'Landesarbeitsgericht', 'Bundesarbeitsgericht', 'Berufung', 'Revision',
      'Klägerin', 'Beklagte', 'Dienstvertrag', 'AVR'
    ];
  }
  if (filePath === file2) {
    return [
      'Mobbing', 'immaterieller Schaden', 'Arbeitsrecht', 'Schadensersatz',
      'Kündigung', 'Arbeitsgericht', 'Landesarbeitsgericht', 'Bundesarbeitsgericht',
      'Berufung', 'Revision', 'Kläger', 'Beklagte', 'Selbstmord', 'Betriebsbedingt'
    ];
  }
  return [];
};

// Function to find common keywords between two files
const findCommonKeywords = (file1Path: string | null, file2Path: string | null) => {
  if (!file1Path || !file2Path) return { commonKeywords: [], similarityCount: 0 };

  const keywords1 = getKeywordsData(file1Path);
  const keywords2 = getKeywordsData(file2Path);

  const commonKeywords = keywords1.filter(keyword =>
    keywords2.includes(keyword)
  );

  return {
    commonKeywords,
    similarityCount: commonKeywords.length
  };
};

// Function to highlight common keywords in text
const highlightCommonKeywords = (text: string, commonKeywords: string[]) => {
  if (!text || commonKeywords.length === 0) return text;

  // Sort keywords by length (longer first) to avoid partial matches
  const sortedKeywords = [...commonKeywords].sort((a, b) => b.length - a.length);

  let highlightedText = text;

  // Create a highlighted version with mark tags
  sortedKeywords.forEach(keyword => {
    const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
    highlightedText = highlightedText.replace(
      regex,
      `<mark style="
        background-color: #fff9c4;
        padding: 2px 4px;
        border-radius: 4px;
      ">${keyword}</mark>`
    );
  });

  return <span dangerouslySetInnerHTML={{ __html: highlightedText }} />;
};

// Mock enrichment data (in a real app, this would come from an API)
const getEnrichmentData = (filePath: string | null) => {
  if (filePath === file1) {
    return {
      keywords: 16,
      legalTaxonomy: 26,
      lawReferences: 17,
      caseReferences: 33
    };
  }
  if (filePath === file2) {
    return {
      keywords: 14,
      legalTaxonomy: 24,
      lawReferences: 18,
      caseReferences: 8
    };
  }
  return {
    keywords: 0,
    legalTaxonomy: 0,
    lawReferences: 0,
    caseReferences: 0
  };
};

// Mock common enrichments (in a real app, this would be calculated)
const getCommonEnrichments = (file1Path: string | null, file2Path: string | null) => {
  if (file1Path && file2Path) {
    return {
      keywords: 8,
      legalTaxonomy: 16,
      lawReferences: 4,
      caseReferences: 2
    };
  }
  return {
    keywords: 0,
    legalTaxonomy: 0,
    lawReferences: 0,
    caseReferences: 0
  };
};

// Mock similarity score (in a real app, this would be calculated)
const getSimilarityScore = (file1Path: string | null, file2Path: string | null) => {
  if (file1Path && file2Path) {
    if (file1Path === file2Path) return 100;
    return 35; // Mock score for different files
  }
  return 0;
};

const mobbing2016Timeline = [
  {
    label: <Text strong>18. Juli 2001</Text>,
    type: 'positive',
    children: (
      <Card size="small">
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Tag>Employment Start</Tag>
            <Text type="secondary" style={{ fontSize: '12px' }}>Duration: 10 years</Text>
          </div>
          <Text>Beginn des Dienstverhältnisses der Klägerin bei der Beklagten.</Text>
        </Space>
      </Card>
    )
  },
  {
    label: <Text strong>24. Juli 2001</Text>,
    type: 'legal',
    children: (
      <Card size="small">
        <Tag>Contract Signed</Tag>
        <Text>Abschluss des Dienstvertrags zwischen den Parteien mit Geltung der AVR-Richtlinien.</Text>
      </Card>
    )
  },
  {
    label: <Text strong>28. April 2009</Text>,
    type: 'info',
    children: (
      <Card size="small">
        <Tag>Training Agreement</Tag>
        <Text>Vereinbarung über sonderpädagogische Zusatzqualifikation (22. Juni 2009 bis 21. Januar 2011).</Text>
      </Card>
    )
  },
  {
    label: <Text strong>Ende 2009/Anfang 2010</Text>,
    type: 'conflict',
    children: (
      <Card size="small">
        <Tag>Conflict Start</Tag>
        <Text>Entstehung von Unstimmigkeiten zwischen Klägerin und Werkstattleiter N bezüglich Vertretungsregelungen.</Text>
      </Card>
    )
  },
  {
    label: <Text strong>23. Februar 2010</Text>,
    type: 'conflict',
    children: (
      <Card size="small">
        <Tag>Work Assignment</Tag>
        <Text>Einsatz der Klägerin im Fahrdienst in B, den sie als zu schwer abbricht.</Text>
      </Card>
    )
  },
  {
    label: <Text strong>Anfang März 2010</Text>,
    type: 'critical',
    children: (
      <Card size="small">
        <Tag>Training Termination</Tag>
        <Text>Kündigung der Ausbildungsvereinbarung durch Herrn N.</Text>
      </Card>
    )
  },
  {
    label: <Text strong>21. April 2010</Text>,
    type: 'critical',
    children: (
      <Card size="small">
        <Tag>Formal Complaint</Tag>
        <Text>Beschwerde der Klägerin bei der Direktion der R-Stiftung über Konflikt mit Herrn N.</Text>
      </Card>
    )
  },
  {
    label: <Text strong>18. Mai 2010</Text>,
    type: 'critical',
    children: (
      <Card size="small">
        <Tag>First Warning</Tag>
        <Text>Abmahnung der Klägerin wegen Störung des Betriebsfriedens.</Text>
      </Card>
    )
  },
  {
    label: <Text strong>27. Mai 2010</Text>,
    type: 'critical',
    children: (
      <Card size="small">
        <Tag>Second Warning</Tag>
        <Text>Zweite Abmahnung wegen nicht unverzüglicher Krankmeldung.</Text>
      </Card>
    )
  },
  {
    label: <Text strong>8. Dezember 2010</Text>,
    type: 'critical',
    children: (
      <Card size="small">
        <Tag>Immediate Termination</Tag>
        <Text>Fristlose Kündigung des Arbeitsverhältnisses durch die Beklagte.</Text>
      </Card>
    )
  },
  {
    label: <Text strong>5. April 2011</Text>,
    type: 'legal',
    children: (
      <Card size="small">
        <Tag>Court Settlement</Tag>
        <Text>Vergleich vor dem Arbeitsgericht: Beendigung des Arbeitsverhältnisses zum 30. Juni 2011 gegen 10.000 € Abfindung.</Text>
      </Card>
    )
  },
  {
    label: <Text strong>30. Juni 2011</Text>,
    type: 'neutral',
    children: (
      <Card size="small">
        <Tag>Employment End</Tag>
        <Text>Ende des Dienstverhältnisses.</Text>
      </Card>
    )
  },
  {
    label: <Text strong>4. April 2013</Text>,
    type: 'claim',
    children: (
      <Card size="small">
        <Tag>Damage Claim</Tag>
        <Text>Erste Schadensersatzforderung der Klägerin wegen Mobbing.</Text>
      </Card>
    )
  },
  {
    label: <Text strong>23. April 2013</Text>,
    type: 'claim',
    children: (
      <Card size="small">
        <Tag>Claim Renewal</Tag>
        <Text>Erneute Geltendmachung von Schadensersatzansprüchen.</Text>
      </Card>
    )
  },
  {
    label: <Text strong>10. September 2013</Text>,
    type: 'legal',
    children: (
      <Card size="small">
        <Tag>Lawsuit Filed</Tag>
        <Text>Klageeingang beim Arbeitsgericht.</Text>
      </Card>
    )
  },
  {
    label: <Text strong>12. Juni 2014</Text>,
    type: 'legal',
    children: (
      <Card size="small">
        <Tag>Lawsuit Extended</Tag>
        <Text>Klageerweiterung beim Arbeitsgericht.</Text>
      </Card>
    )
  },
  {
    label: <Text strong>14. April 2015</Text>,
    type: 'legal',
    children: (
      <Card size="small">
        <Tag>Appeal Court Decision</Tag>
        <Text>Urteil des Landesarbeitsgerichts Nürnberg - Zurückweisung der Berufung.</Text>
      </Card>
    )
  },
  {
    label: <Text strong>15. September 2016</Text>,
    type: 'legal',
    children: (
      <Card size="small">
        <Tag>Final Decision</Tag>
        <Text>Bundesarbeitsgericht weist Revision der Klägerin endgültig zurück.</Text>
      </Card>
    )
  }
];

const mobbing2008Timeline = [
  {
    label: <Text strong>1996</Text>,
    type: 'positive',
    children: (
      <Card size="small">
        <Tag>Employment Start</Tag>
        <Text>Beginn des Beschäftigungsverhältnisses des Ehemanns der Klägerin bei der Beklagten als Betriebshandwerker.</Text>
      </Card>
    )
  },
  {
    label: <Text strong>2. Juli 2004</Text>,
    type: 'critical',
    children: (
      <Card size="small">
        <Tag>Termination Notice</Tag>
        <Text>Betriebsbedingte Kündigung des Ehemanns durch die Beklagte zum 31. Juli 2004.</Text>
      </Card>
    )
  },
  {
    label: <Text strong>5. Juli 2004 - 18. Juli 2004</Text>,
    type: 'conflict',
    children: (
      <Card size="small">
        <Tag>Sick Leave</Tag>
        <Text>Krankheitsbedingte Arbeitsunfähigkeit des Ehemanns.</Text>
      </Card>
    )
  },
  {
    label: <Text strong>19. Juli 2004</Text>,
    type: 'legal',
    children: (
      <Card size="small">
        <Tag>Reinstatement</Tag>
        <Text>Rücknahme der Kündigung durch die Beklagte nach Kündigungsschutzklage; Wiederaufnahme der Arbeit mit neuen Aufgaben in der Stanzerei.</Text>
      </Card>
    )
  },
  {
    label: <Text strong>19. Juli 2004</Text>,
    type: 'conflict',
    children: (
      <Card size="small">
        <Tag>Work Changes</Tag>
        <Text>Aufforderung zur Abgabe des Zentralschlüssels und Einsatz in Transport-, Montagearbeiten sowie Müllabfuhr.</Text>
      </Card>
    )
  },
  {
    label: <Text strong>2. August 2004</Text>,
    type: 'critical',
    children: (
      <Card size="small">
        <Tag>Access Restrictions</Tag>
        <Text>Belegung des Umkleidespinds und Sperrung des Einlasschips; Zuweisung eines neuen Spinds.</Text>
      </Card>
    )
  },
  {
    label: <Text strong>21. September 2004</Text>,
    type: 'critical',
    children: (
      <Card size="small">
        <Tag>Tragic Event</Tag>
        <Text>Selbstmord des Ehemanns der Klägerin.</Text>
      </Card>
    )
  },
  {
    label: <Text strong>Nach 21. September 2004</Text>,
    type: 'claim',
    children: (
      <Card size="small">
        <Tag>Inheritance & Claims</Tag>
        <Text>Klägerin wird Alleinerbin und macht Schadensersatzansprüche geltend.</Text>
      </Card>
    )
  },
  {
    label: <Text strong>25. Januar 2007</Text>,
    type: 'legal',
    children: (
      <Card size="small">
        <Tag>Regional Court Decision</Tag>
        <Text>Urteil des Thüringer Landesarbeitsgerichts - Zurückweisung der Klage.</Text>
      </Card>
    )
  },
  {
    label: <Text strong>24. April 2008</Text>,
    type: 'legal',
    children: (
      <Card size="small">
        <Tag>Final Decision</Tag>
        <Text>Bundesarbeitsgericht weist Revision der Klägerin endgültig zurück.</Text>
      </Card>
    )
  }
];

// Function to get timeline data based on the selected file
const getTimelineData = (filePath: string | null) => {
  if (filePath === file1) {
    return mobbing2016Timeline;
  }
  if (filePath === file2) {
    return mobbing2008Timeline;
  }
  return [];
};

// Function to get timeline summary stats (updated to use `type` instead of `color`)
const getTimelineStats = (filePath: string | null) => {
  const timelineData = getTimelineData(filePath);
  return {
    totalEvents: timelineData.length,
    duration: filePath === file1 ? '2001-2016 (15 years)' : '1996-2008 (12 years)',
    keyEvents: timelineData.filter(item => item.type === 'critical' || item.type === 'legal').length,
    legalProceedings: timelineData.filter(item => item.type === 'legal').length
  };
};

const UC3 = () => {
  const [selectedFile, setSelectedFile] = useState<string | null>(file1);
  const [fileContent, setFileContent] = useState('');
  const [panel, setPanel] = useState<'timeline' | 'similarity'>('timeline');

  // Similarity matching states
  const [firstFile, setFirstFile] = useState<string | null>(file1);
  const [secondFile, setSecondFile] = useState<string | null>(file2);
  const [firstFileContent, setFirstFileContent] = useState('');
  const [secondFileContent, setSecondFileContent] = useState('');
  const [commonKeywordsData, setCommonKeywordsData] = useState({
    commonKeywords: [] as string[],
    similarityCount: 0
  });

  useEffect(() => {
    const loadFile = async () => {
      if (selectedFile) {
        const content = await readTextFile(selectedFile);
        setFileContent(content);
      } else {
        setFileContent('');
      }
    };
    loadFile();
  }, [selectedFile]);

  useEffect(() => {
    const loadFirstFile = async () => {
      if (firstFile) {
        const content = await readTextFile(firstFile);
        setFirstFileContent(content);
      } else {
        setFirstFileContent('');
      }
    };
    loadFirstFile();
  }, [firstFile]);

  useEffect(() => {
    const loadSecondFile = async () => {
      if (secondFile) {
        const content = await readTextFile(secondFile);
        setSecondFileContent(content);
      } else {
        setSecondFileContent('');
      }
    };
    loadSecondFile();
  }, [secondFile]);

  // Update common keywords when files change
  useEffect(() => {
    const keywordsData = findCommonKeywords(firstFile, secondFile);
    setCommonKeywordsData(keywordsData);
  }, [firstFile, secondFile]);

  const handleFileChange = (value: string) => {
    setSelectedFile(value);
  };

  const handleFirstFileChange = (value: string) => {
    setFirstFile(value);
  };

  const handleSecondFileChange = (value: string) => {
    setSecondFile(value);
  };

  const getFirstTwoLines = (text: string): string => {
    if (!text) return '';
    const lines = text.split('\n');
    return `${lines[0]}\n${lines[1] || ''}`;
  };

  const fileOptions = [
    { label: 'Mobbing als immaterieller Schaden (2016)', value: file1 },
    { label: 'Mobbing als immaterieller Schaden (2008)', value: file2 },
  ];

  const getFileLabel = (value: string | null) => {
    if (!value) return '';
    const file = fileOptions.find(option => option.value === value);
    return file ? file.label : '';
  };

  const timelineStats = getTimelineStats(selectedFile);

  const renderPanel = () => {
    if (panel === 'timeline') {
      const timelineItems = getTimelineData(selectedFile);
      const selectedFileLabel = fileOptions.find(file => file.value === selectedFile)?.label || '';

      return (
        <>
          <div style={{ marginBottom: '24px' }}>
            <Title level={3} style={{ marginBottom: '8px' }}>
              <ClockCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
              Legal Timeline Analysis
            </Title>
            <Text type="secondary">
              Visual chronology of legal events and proceedings with contextual categorization
            </Text>
          </div>

          {/* File Selection and Stats */}
          <Card style={{ marginBottom: '24px' }}>
            <Row gutter={16} align="middle">
              <Col span={8}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text strong>Select Case Document:</Text>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Select a file"
                    onChange={handleFileChange}
                    value={selectedFile}
                    size="large"
                  >
                    {fileOptions.map((file) => (
                      <Option key={file.value} value={file.value}>
                        {file.label}
                      </Option>
                    ))}
                  </Select>
                </Space>
              </Col>
              <Col span={16}>
                <Row gutter={16}>
                  <Col span={6}>
                    <Statistic title="Total Events" value={timelineStats.totalEvents} />
                  </Col>
                  <Col span={6}>
                    <Statistic title="Case Duration" value={timelineStats.duration} />
                  </Col>
                  <Col span={6}>
                    <Statistic title="Key Events" value={timelineStats.keyEvents} />
                  </Col>
                  <Col span={6}>
                    <Statistic title="Legal Proceedings" value={timelineStats.legalProceedings} />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card>

          <Row gutter={24}>
            {/* Document Content */}
            <Col span={10}>
              <Card
                title={
                  <Space>
                    <FileTextOutlined />
                    <Text strong>{selectedFileLabel}</Text>
                  </Space>
                }
                extra={<Tag>Original Document</Tag>}
                style={{ height: 'fit-content' }}
              >
                <Alert
                  message="Document Preview"
                  description="First two lines of the selected legal document"
                  type="info"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />

                <Card
                  size="small"
                  style={{ marginBottom: '16px' }}
                >
                  <Text strong style={{ whiteSpace: 'pre-wrap' }}>
                    {getFirstTwoLines(fileContent)}
                  </Text>
                </Card>

                <Card
                  title="Full Document Content"
                  size="small"
                  style={{ maxHeight: '500px', overflow: 'auto' }}
                >
                  <Paragraph style={{ whiteSpace: 'pre-wrap', fontSize: '12px', lineHeight: '1.6' }}>
                    {fileContent}
                  </Paragraph>
                </Card>
              </Card>
            </Col>

            {/* Timeline */}
            <Col span={14}>
              <Card
                title={
                  <Space>
                    <CalendarOutlined />
                    <Text strong>Legal Timeline</Text>
                  </Space>
                }
              >
                <Alert
                  message="Timeline Legend"
                  description="Events show different types of legal proceedings and milestones"
                  type="info"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />

                <div style={{ maxHeight: '600px', overflow: 'auto', padding: '8px' }}>
                  <Timeline
                    mode="left"
                    items={timelineItems}
                    style={{ padding: '16px 0' }}
                  />
                </div>

                <Divider />

                <Row gutter={16}>
                  <Col span={8}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <Title level={5} style={{ margin: 0 }}>Employment Period</Title>
                      <Text>{timelineStats.duration}</Text>
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <Title level={5} style={{ margin: 0 }}>Legal Decisions</Title>
                      <Text>{timelineStats.legalProceedings} proceedings</Text>
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card size="small" style={{ textAlign: 'center' }}>
                      <Title level={5} style={{ margin: 0 }}>Case Status</Title>
                      <Text type="success">Closed</Text>
                    </Card>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </>
      );
    } else if (panel === 'similarity') {
      const similarityScore = getSimilarityScore(firstFile, secondFile);
      const enrichment1 = getEnrichmentData(firstFile);
      const enrichment2 = getEnrichmentData(secondFile);
      const commonEnrichments = getCommonEnrichments(firstFile, secondFile);
      const keywords1 = getKeywordsData(firstFile);
      const keywords2 = getKeywordsData(secondFile);

      return (
        <>
          <div style={{ marginBottom: '24px' }}>
            <Title level={3} style={{ marginBottom: '8px' }}>
              <FileTextOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
              Document Similarity Analysis
            </Title>
            <Text type="secondary">
              Compare legal documents and analyze their similarities, enrichments, and relationships
            </Text>
          </div>

          {/* File selection and similarity score */}
          <Card style={{ marginBottom: '24px' }}>
            <Row gutter={16} align="middle">
              <Col span={10}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text strong>Select First Document:</Text>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Select first file"
                    onChange={handleFirstFileChange}
                    value={firstFile}
                    size="large"
                  >
                    {fileOptions.map((file) => (
                      <Option key={file.value} value={file.value}>
                        {file.label}
                      </Option>
                    ))}
                  </Select>
                </Space>
              </Col>
              <Col span={4}>
                <div style={{ textAlign: 'center' }}>
                  <Statistic
                    title="Similarity Score"
                    value={similarityScore}
                    suffix="%"
                    valueStyle={{
                      color: similarityScore > 70 ? '#3f8600' :
                        similarityScore > 40 ? '#faad14' : '#cf1322',
                      fontSize: '32px'
                    }}
                  />
                  <Text type="secondary">
                    {similarityScore > 70 ? 'High Similarity' :
                      similarityScore > 40 ? 'Medium Similarity' : 'Low Similarity'}
                  </Text>
                </div>
              </Col>
              <Col span={10}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text strong>Select Second Document:</Text>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Select second file"
                    onChange={handleSecondFileChange}
                    value={secondFile}
                    size="large"
                  >
                    {fileOptions.map((file) => (
                      <Option key={file.value} value={file.value}>
                        {file.label}
                      </Option>
                    ))}
                  </Select>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* File preview headers */}
          <Row gutter={16} style={{ marginBottom: '16px' }}>
            <Col span={12}>
              <Card size="small">
                <Text strong style={{ fontSize: '16px' }}>{getFileLabel(firstFile)}</Text>
                <div style={{ padding: '12px', borderRadius: '4px', marginTop: '8px' }}>
                  <Text style={{ whiteSpace: 'pre-wrap' }}>{getFirstTwoLines(firstFileContent)}</Text>
                </div>
              </Card>
            </Col>
            <Col span={12}>
              <Card size="small">
                <Text strong style={{ fontSize: '16px' }}>{getFileLabel(secondFile)}</Text>
                <div style={{ padding: '12px', borderRadius: '4px', marginTop: '8px' }}>
                  <Text style={{ whiteSpace: 'pre-wrap' }}>{getFirstTwoLines(secondFileContent)}</Text>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Common keywords summary */}
          <Card style={{ marginBottom: '16px' }}>
            <Row gutter={16} align="middle">
              <Col span={12}>
                <Statistic
                  title="Common Keywords"
                  value={commonKeywordsData.similarityCount}
                  prefix={<FileTextOutlined />}
                />
              </Col>
              <Col span={12}>
                <Alert
                  message="Keyword Highlighting"
                  description="Yellow highlighted keywords appear in both documents (extracted legal terms)"
                  type="info"
                  showIcon
                />
              </Col>
            </Row>

            {/* Display common keywords */}
            {commonKeywordsData.commonKeywords.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <Text strong>Common Keywords: </Text>
                <Space wrap size={[8, 8]} style={{ marginTop: '8px' }}>
                  {commonKeywordsData.commonKeywords.map((keyword, index) => (
                    <Tag key={index} color="blue">{keyword}</Tag>
                  ))}
                </Space>
              </div>
            )}
          </Card>

          {/* File content with highlighted common keywords */}
          <Row gutter={16} style={{ marginBottom: '24px' }}>
            <Col span={12}>
              <Card
                title="Document 1 Content"
                style={{ height: '400px', overflow: 'auto' }}
                extra={<Tag>{(firstFileContent.length / 1000).toFixed(1)} KB</Tag>}
              >
                <Paragraph style={{ whiteSpace: 'pre-wrap', fontSize: '12px', lineHeight: '1.6' }}>
                  {firstFileContent ? highlightCommonKeywords(firstFileContent, commonKeywordsData.commonKeywords) : 'Select a file to view content'}
                </Paragraph>
              </Card>
            </Col>
            <Col span={12}>
              <Card
                title="Document 2 Content"
                style={{ height: '400px', overflow: 'auto' }}
                extra={<Tag>{(secondFileContent.length / 1000).toFixed(1)} KB</Tag>}
              >
                <Paragraph style={{ whiteSpace: 'pre-wrap', fontSize: '12px', lineHeight: '1.6' }}>
                  {secondFileContent ? highlightCommonKeywords(secondFileContent, commonKeywordsData.commonKeywords) : 'Select a file to view content'}
                </Paragraph>
              </Card>
            </Col>
          </Row>

          <Divider />

          {/* Enrichments comparison */}
          <Title level={4}>Enrichments Comparison</Title>
          <Alert
            message="Enrichments Analysis"
            description="Comparison of extracted legal elements and metadata from both documents"
            type="info"
            showIcon
            style={{ marginBottom: '16px' }}
          />

          <Row gutter={16}>
            <Col span={8}>
              <Card title="Enrichments File 1" size="small">
                <div style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <Text strong>Keywords: </Text>
                  <Text style={{ float: 'right', fontWeight: 'bold' }}>{enrichment1.keywords}</Text>
                </div>
                <div style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <Text strong>Legal Taxonomy: </Text>
                  <Text style={{ float: 'right', fontWeight: 'bold' }}>{enrichment1.legalTaxonomy}</Text>
                </div>
                <div style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <Text strong>Law References: </Text>
                  <Text style={{ float: 'right', fontWeight: 'bold' }}>{enrichment1.lawReferences}</Text>
                </div>
                <div style={{ padding: '12px 0' }}>
                  <Text strong>Case References: </Text>
                  <Text style={{ float: 'right', fontWeight: 'bold' }}>{enrichment1.caseReferences}</Text>
                </div>
              </Card>
            </Col>
            <Col span={8}>
              <Card title="Enrichments File 2" size="small">
                <div style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <Text strong>Keywords: </Text>
                  <Text style={{ float: 'right', fontWeight: 'bold' }}>{enrichment2.keywords}</Text>
                </div>
                <div style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <Text strong>Legal Taxonomy: </Text>
                  <Text style={{ float: 'right', fontWeight: 'bold' }}>{enrichment2.legalTaxonomy}</Text>
                </div>
                <div style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <Text strong>Law References: </Text>
                  <Text style={{ float: 'right', fontWeight: 'bold' }}>{enrichment2.lawReferences}</Text>
                </div>
                <div style={{ padding: '12px 0' }}>
                  <Text strong>Case References: </Text>
                  <Text style={{ float: 'right', fontWeight: 'bold' }}>{enrichment2.caseReferences}</Text>
                </div>
              </Card>
            </Col>
            <Col span={8}>
              <Card title="Common Enrichments" size="small">
                <div style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <Text strong>Keywords: </Text>
                  <Text style={{ float: 'right', fontWeight: 'bold' }}>{commonEnrichments.keywords}</Text>
                </div>
                <div style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <Text strong>Legal Taxonomy: </Text>
                  <Text style={{ float: 'right', fontWeight: 'bold' }}>{commonEnrichments.legalTaxonomy}</Text>
                </div>
                <div style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <Text strong>Law References: </Text>
                  <Text style={{ float: 'right', fontWeight: 'bold' }}>{commonEnrichments.lawReferences}</Text>
                </div>
                <div style={{ padding: '12px 0' }}>
                  <Text strong>Case References: </Text>
                  <Text style={{ float: 'right', fontWeight: 'bold' }}>{commonEnrichments.caseReferences}</Text>
                </div>
              </Card>
            </Col>
          </Row>
        </>
      );
    }
  };

  return (
    <Layout style={{ padding: '24px', minHeight: '100vh', background: '#f0f2f5' }}>
      <Content>
        <Card>
          {/* Panel Toggle Buttons */}
          <Row gutter={24} style={{ marginBottom: '24px' }}>
            <Col>
              <Button
                type={panel === 'timeline' ? 'primary' : 'default'}
                icon={<CalendarOutlined />}
                onClick={() => setPanel('timeline')}
                size="large"
              >
                Legal Timeline
              </Button>
            </Col>
            <Col>
              <Button
                type={panel === 'similarity' ? 'primary' : 'default'}
                icon={<FileTextOutlined />}
                onClick={() => setPanel('similarity')}
                size="large"
              >
                Similarity Matching
              </Button>
            </Col>
          </Row>

          {/* Main Content */}
          {renderPanel()}
        </Card>
      </Content>
    </Layout>
  );
};

export default UC3;